import React, { FC, useState } from "react";
import { Modal } from "@mantine/core";
import { Share, Link2, Mail, Check } from "lucide-react";
import { trackShare, ShareChannel, ShareSurface } from "utils/analytics/analytics";

export interface ShareButtonProps {
  propertyId: number | string;
  slug: string;
  title?: string;
  city?: string;
  variant?: "icon" | "full";
  surface?: ShareSurface;
  className?: string;
  /** Link to share. Defaults to the current listing page address. */
  path?: string;
}

const ShareButton: FC<ShareButtonProps> = ({
  propertyId,
  slug,
  title = "this place",
  city,
  variant = "icon",
  surface = "stay_card",
  className = "",
  path,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = `${window.location.origin}${path || `/listing-stay-detail/${slug}`}`;
  const shareText = city ? `${title} in ${city}` : title;

  const record = (channel: ShareChannel) =>
    trackShare({ propertyId, channel, surface });

  const openShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      record("copy_link");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const handleWhatsApp = () => {
    record("whatsapp");
    window.open(
      `https://wa.me/?text=${encodeURIComponent(`${shareText} — ${url}`)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleEmail = () => {
    record("email");
    const subject = encodeURIComponent(`Check out ${shareText}`);
    const body = encodeURIComponent(
      `I thought you might like this place:\n\n${shareText}\n${url}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <>
      {variant === "full" ? (
        <button
          type="button"
          onClick={openShare}
          aria-label={`Share ${title}`}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors ${className}`}
        >
          <Share size={16} />
          <span className="underline">Share</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={openShare}
          aria-label={`Share ${title}`}
          title="Share"
          className={`w-8 h-8 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-neutral-700 shadow transition-all active:scale-90 ${className}`}
        >
          <Share size={16} />
        </button>
      )}

      <Modal
        opened={isOpen}
        onClose={() => setIsOpen(false)}
        size="md"
        centered
        withCloseButton={false}
        radius="xl"
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
        classNames={{
          content: "bg-white dark:bg-neutral-900",
          body: "bg-white dark:bg-neutral-900 p-0",
        }}
      >
        <div className="p-6" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between mb-5">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Share this place
            </h3>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
            >
              ×
            </button>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 line-clamp-2">
            {shareText}
          </p>

          <div className="space-y-2">
            <ShareOption
              icon={copied ? <Check size={20} /> : <Link2 size={20} />}
              label={copied ? "Link copied" : "Copy link"}
              onClick={handleCopy}
              highlight={copied}
            />
            <ShareOption icon={<WhatsAppIcon />} label="WhatsApp" onClick={handleWhatsApp} />
            <ShareOption icon={<Mail size={20} />} label="Email" onClick={handleEmail} />
          </div>
        </div>
      </Modal>
    </>
  );
};

const ShareOption: FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  highlight?: boolean;
}> = ({ icon, label, onClick, highlight }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
      highlight
        ? "border-green-400 bg-green-50 text-green-700"
        : "border-neutral-200 dark:border-neutral-700 hover:border-[#f5694b] hover:bg-orange-50/50 dark:hover:bg-neutral-800 text-gray-800 dark:text-gray-100"
    }`}
  >
    <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 dark:bg-neutral-800">
      {icon}
    </span>
    <span className="text-sm font-medium">{label}</span>
  </button>
);

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 016.99 2.898 9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.887 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.4" />
  </svg>
);

export default ShareButton;